import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation17({ animation, badge, all_animations }: Props) {
  const [progress, setProgress] = useState(0)
  const [showComplete, setShowComplete] = useState(false)

  useEffect(() => {
    const duration = 2000
    const steps = 100
    const stepDuration = duration / steps
    let currentStep = 0

    const interval = setInterval(() => {
      currentStep++
      const newProgress = (currentStep / steps) * 100
      setProgress(newProgress)

      if (currentStep >= steps) {
        clearInterval(interval)
        setShowComplete(true)
      }
    }, stepDuration)

    return () => clearInterval(interval)
  }, [])

  const circumference = 2 * Math.PI * 80
  const strokeDashoffset = circumference - (progress / 100) * circumference

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes rotateGlow {
          0% { filter: drop-shadow(0 0 10px rgba(255, 241, 152, 0.5)); }
          50% { filter: drop-shadow(0 0 20px rgba(255, 241, 152, 0.8)); }
          100% { filter: drop-shadow(0 0 10px rgba(255, 241, 152, 0.5)); }
        }
        @keyframes scaleIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        .progress-ring {
          animation: rotateGlow 2s ease-in-out infinite;
        }
        .scale-in {
          animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        {/* Anneau de progression */}
        <div className="relative w-40 h-40 sm:w-52 sm:h-52">
          <svg className="w-full h-full transform -rotate-90 progress-ring" viewBox="0 0 180 180">
            <circle
              cx="90"
              cy="90"
              r="80"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="12"
            />
            <circle
              cx="90"
              cy="90"
              r="80"
              fill="none"
              stroke="#FFF198"
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              className="transition-all duration-100"
            />
          </svg>

          {/* Contenu central */}
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {!showComplete ? (
              <div className="text-4xl font-bold text-tessan-cream">
                {Math.round(progress)}%
              </div>
            ) : (
              <div className="scale-in w-24 h-24 bg-tessan-cream rounded-xl flex items-center justify-center">
                <span className="text-5xl">{badge.emoji}</span>
              </div>
            )}
          </div>
        </div>

        {/* Infos du badge */}
        <div className={`mt-8 text-center transition-all duration-500 ${showComplete ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-2xl sm:text-4xl font-black text-tessan-yellow">{badge.name}</div>
          <div className="text-lg sm:text-xl text-tessan-cream mt-2">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
